import { Button as ButtonANTD, Col, Modal as ModalAntd, Row } from "antd";
import styled, { css } from "styled-components";

import contrastAutoAdjust from "shared/utils/contrastAutoAdjust";

interface IModal {
  modalShare?: boolean;
}

interface IHeader {
  themeTopBar: string;
  backgroundColor: string;
  themeHeaderStyle: string;
}

export const Header = styled.div<IHeader>`
  width: 100vw;
  min-height: 400px;
  background: ${({ theme }) => theme.colors.headerBackgroundColor};

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h1.title,
  p.description {
    text-align: center;
    color: ${({ theme }) => theme.colors.headerTextColor};
  }

  margin: ${({ themeTopBar, theme }) =>
    (themeTopBar === "transparent" || themeTopBar === "gradient") &&
    !(theme.mode === "dark")
      ? "-80px 0 32px 0"
      : "-32px 0 32px 0"};

  h1.title {
    font-family: Poppins;
    font-weight: bold;
    font-size: 40px !important;
    line-height: 122%;
    margin-top: 10px;
    letter-spacing: -0.5px;
  }
  span {
    font-family: Roboto;
    font-weight: 900;
    font-size: 13px;
    line-height: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 130px;
    height: 21px;
    border-radius: 4px;

    background: ${({ theme, backgroundColor }) =>
      contrastAutoAdjust({
        background: theme.mode === "dark" ? "#404040" : backgroundColor,
        text: "#ffb400",
      })};

    color: ${({ theme, backgroundColor, themeHeaderStyle }) =>
      theme.mode === "dark"
        ? "#404040"
        : themeHeaderStyle === "automatic"
        ? theme.colors.primary
        : backgroundColor};
  }

  p.description {
    font-family: Roboto;
    font-weight: normal;
    font-size: 20px;
    line-height: 100%;
    max-width: 80vw;
  }
`;

export const EmptyDataRow = styled(Row)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80%;

  span {
    color: ${({ theme }) => theme.colors.textColor};
  }
`;

export const Modal = styled(ModalAntd)<IModal>`
  ${({ modalShare }) =>
    modalShare &&
    css`
      .ant-modal-title {
        margin-right: 20px;
      }
    `}

  .ant-modal-content {
    .ant-modal-header {
      border-bottom: ${({ theme }) =>
        theme.mode === "dark"
          ? "1px solid rgb(36, 36, 36)"
          : "1px solid #d9d9d9"} !important;

      background: ${({ theme }) =>
        theme.colors.inputBackgroundColor} !important;
    }

    background: ${({ theme }) => theme.colors.inputBackgroundColor};

    input,
    textarea {
      background: ${({ theme }) =>
        theme.colors.inputBackgroundColor} !important;
      border-color: ${({ theme }) =>
        theme.colors.collectionsInputBorderColor} !important;
      outline: none;
      box-shadow: none;
    }

    .ant-btn.ant-btn-default {
      background-color: ${({ theme }) => theme.colors.primary};
      border: ${({ theme }) => theme.colors.primary};

      transition: 0.5s all;

      &:hover {
        transition: 0.5s all;
        opacity: 0.8;
      }
    }

    p,
    span,
    small,
    .ant-modal-body,
    .ant-modal-close-x {
      color: ${({ theme }) => theme.colors.textColor};
    }

    .ant-switch-checked {
      background: ${(props) => props?.theme.colors.primary};
    }

    .ant-input-group-addon {
      background: ${(props) => props?.theme.colors.primary};
      border: 1px solid ${({ theme }) => theme.colors.modalBackgroundColor};
    }

    .ant-modal-header {
      background-color: ${({ theme }) => theme.colors.modalBackgroundColor};
      border-bottom: 1px solid
        ${({ theme }) => theme.colors.modalBackgroundColor};
      .ant-modal-title {
        color: ${({ theme }) => theme.colors.textColor};
      }
    }

    .ant-modal-body {
      button span {
        color: ${({ theme }) => theme.colors.textMasterColor} !important;
      }
    }

    .ant-modal-footer {
      background-color: ${({ theme }) => theme.colors.modalBackgroundColor};
      border-top: 1px solid ${({ theme }) => theme.colors.modalBackgroundColor};

      button.ant-btn {
        span {
          color: ${({ theme }) => theme.colors.textColor};
        }
        background-color: transparent !important;
      }

      button.ant-btn:hover {
        border-color: ${(props) => props?.theme.colors.primary} !important;
      }

      button.ant-btn-primary {
        background-color: ${(props) => props?.theme.colors.primary} !important;
        span {
          color: ${({ theme }) => theme.colors.textColor};
        }
      }
    }

    textarea,
    input {
      color: ${({ theme }) => theme.colors.textColor};
    }
  }
`;

export const CollectionsCentralizer = styled.div<{ themeTopBar: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;

  padding: 0 1rem;

  .gx-product-item {
    background: transparent !important;
  }

  .ant-radio-button-wrapper-checked {
    span + span {
      &,
      span svg {
        color: ${({ theme }) => theme.colors.btnTextColor};
      }
    }
  }

  .ant-radio-button-wrapper {
    background: ${({ theme }) => theme.colors.backgroundColor};
    border-color: ${({ theme }) => theme.colors.borderColor};
    color: ${({ theme }) => theme.colors.textColor};
  }

  .content {
    h1,
    p {
      color: ${({ theme }) => theme.colors.textColor};
    }

    button span svg,
    button span,
    button:is(:hover, :active, :focus) span {
      color: ${({ theme }) => theme.colors.textMasterColor} !important;
    }

    a {
      svg,
      span {
        color: ${({ theme }) => theme.colors.textColor};
      }
    }

    input,
    .ant-input-affix-wrapper {
      box-shadow: none;
      outline: none;
      border-color: ${({ theme }) =>
        theme.colors.collectionsInputBorderColor}!important;
      background: ${({ theme }) => theme.colors.backgroundColor} !important;
    }
  }

  .ant-select {
    .ant-select-selector {
      background-color: ${(props) =>
        props.theme.colors.modalBackgroundColor} !important;
      border-color: ${(props) =>
        props.theme.colors.modalBackgroundColor} !important;
      color: ${({ theme }) => theme.colors.textColor};
    }

    .ant-select-clear {
      background: transparent;
    }
  }

  input {
    color: ${({ theme }) => theme.colors.textColor};
  }

  .gx-product-item {
    background: transparent !important;
    .gx-product-body,
    .gx-product-image,
    .gx-product-image div + div {
      background: transparent !important;
    }
    h4,
    span {
      color: ${({ theme }) => theme.colors.textColor};
    }
  }

  .ant-row {
    margin-right: 0;
    margin-left: 0;
  }

  .content {
    max-width: 1400px;
    width: 100%;
    margin-bottom: 60px;

    h1 {
      font-size: 22px;
    }

    .searchCollection {
      padding-right: 20px;
    }
  }

  .content.collection {
    ${({ theme, themeTopBar }) =>
      (themeTopBar === "gradient" || themeTopBar === "transparent") &&
      !(theme.mode === "dark") &&
      css`
        margin-top: 13px;
      `}
  }

  .btnCreateCollection {
    display: flex;
    justify-content: flex-end;
    width: max-content;
  }

  .row-card {
    justify-content: center;
  }

  .section-card {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .render-collection-card {
      width: 350px !important;
    }

    @media (max-width: 600px) {
      justify-content: center;
    }
  }

  .ant-input-group-addon {
    vertical-align: top;
  }

  @media (max-width: 600px) {
    .content {
      padding: 0 20px;
    }
  }

  @media (max-width: 780px) {
    .searchCollection {
      padding: 0 20px 20px 0;
    }
    .btnCreateCollection {
      justify-content: center;
    }
    .ant-radio-group {
      width: max-content;
    }
  }

  @media (min-width: 1200px) {
    .colSwitchRadioItem {
      width: 22% !important;
    }
  }

  @media (max-width: 1550px) {
    .section-card .render-collection-card {
      width: 280px !important;
    }
  }
`;

export const ContentRow = styled(Row)`
  @media (max-width: 1024px) {
    display: block;
    margin: auto;
  }
`;

export const ColRadio = styled(Col)`
  margin-bottom: 20px;
  padding: 0;

  .ant-radio-button-wrapper:first-child {
    border-radius: 6px 0 0 6px;
  }

  .ant-radio-button-wrapper:last-child {
    border-radius: 0 6px 6px 0;
  }

  .ant-radio-button-wrapper-checked:not(
      .ant-radio-button-wrapper-disabled
    )::before {
    background: ${({ theme }) => theme.colors.primary};
  }

  .ant-radio-button-wrapper.ant-radio-button-wrapper-checked {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary} !important;

    svg,
    span {
      color: ${({ theme }) => theme.colors.textMasterColor};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const Button = styled(ButtonANTD)`
  border-color: ${({ theme }) => theme.colors.primary} !important;
  background: ${({ theme }) => theme.colors.primary} !important;

  span {
    color: ${({ theme }) => theme.colors.btnTextColor} !important;
  }

  transition: opacity 0.4s;

  &:hover {
    opacity: 0.8;
  }
`;
